/**
 * Custom draw modes para @mapbox/mapbox-gl-draw:
 *  - draw_circle_two_click: 1º clique = centro, 2º clique = borda (define raio dinamicamente)
 *  - draw_marker_circle:    1 clique = círculo de raio fixo (default 3000m)
 *
 * Ambos geram um Polygon (turf.circle com 64 lados) e atribuem
 * properties.shapeType para identificação posterior.
 */

import turfCircle from '@turf/circle';

const RAIO_PADRAO_MARCADOR_M = 3000;
const STEPS = 64;

function metersBetween(a, b) {
    // Haversine simplificado em metros
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b[1] - a[1]);
    const dLng = toRad(b[0] - a[0]);
    const lat1 = toRad(a[1]);
    const lat2 = toRad(b[1]);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
}

export function makeCircleFeature(centerLngLat, radiusMeters, shapeType) {
    const feature = turfCircle(centerLngLat, radiusMeters / 1000, { steps: STEPS, units: 'kilometers' });
    feature.properties = {
        ...feature.properties,
        shapeType,
        center: centerLngLat,
        radiusMeters,
    };
    return feature;
}

// ============================================================
// Tema data-driven do MapboxDraw
// Baseado no tema padrão (@mapbox/mapbox-gl-draw/src/lib/theme.js), porém as
// camadas de polígono leem os atributos da própria feature (prefixo `user_`)
// quando ela NÃO está ativa (em edição). Em edição mantém o destaque laranja
// padrão. Permite cor/opacidade/espessura por forma (ver ShapeAttributesEditor).
// ============================================================
const DRAW_BLUE = '#3bb2d0';
const DRAW_ORANGE = '#fbb03b';
const DRAW_WHITE = '#fff';

export const drawStyles = [
    // Preenchimento do polígono
    {
        id: 'gl-draw-polygon-fill',
        type: 'fill',
        filter: ['all', ['==', '$type', 'Polygon']],
        paint: {
            'fill-color': [
                'case',
                ['==', ['get', 'active'], 'true'], DRAW_ORANGE,
                ['has', 'user_fillColor'], ['get', 'user_fillColor'],
                DRAW_BLUE,
            ],
            'fill-opacity': [
                'case',
                ['==', ['get', 'active'], 'true'], 0.1,
                ['has', 'user_fillOpacity'], ['get', 'user_fillOpacity'],
                0.3,
            ],
        },
    },
    // Bordas (linhas e contorno de polígono)
    {
        id: 'gl-draw-lines',
        type: 'line',
        filter: ['any', ['==', '$type', 'LineString'], ['==', '$type', 'Polygon']],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
            'line-color': [
                'case',
                ['==', ['get', 'active'], 'true'], DRAW_ORANGE,
                ['has', 'user_strokeColor'], ['get', 'user_strokeColor'],
                DRAW_BLUE,
            ],
            'line-dasharray': ['case', ['==', ['get', 'active'], 'true'], [0.2, 2], [2, 0]],
            'line-width': [
                'case',
                ['has', 'user_strokeWeight'], ['get', 'user_strokeWeight'],
                2,
            ],
        },
    },
    // Pontos (feature)
    {
        id: 'gl-draw-point-outer',
        type: 'circle',
        filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'feature']],
        paint: {
            'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 7, 5],
            'circle-color': DRAW_WHITE,
        },
    },
    {
        id: 'gl-draw-point-inner',
        type: 'circle',
        filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'feature']],
        paint: {
            'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 5, 3],
            'circle-color': ['case', ['==', ['get', 'active'], 'true'], DRAW_ORANGE, DRAW_BLUE],
        },
    },
    // Vértices (visíveis ao editar)
    {
        id: 'gl-draw-vertex-outer',
        type: 'circle',
        filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'vertex'], ['!=', 'mode', 'simple_select']],
        paint: {
            'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 7, 5],
            'circle-color': DRAW_WHITE,
        },
    },
    {
        id: 'gl-draw-vertex-inner',
        type: 'circle',
        filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'vertex'], ['!=', 'mode', 'simple_select']],
        paint: {
            'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 5, 3],
            'circle-color': DRAW_ORANGE,
        },
    },
    // Midpoints (clicar/arrastar adiciona vértice)
    {
        id: 'gl-draw-midpoint',
        type: 'circle',
        filter: ['all', ['==', 'meta', 'midpoint']],
        paint: { 'circle-radius': 3, 'circle-color': DRAW_ORANGE },
    },
];

// ============================================================
// Modo: círculo por 2 cliques (centro + borda)
// ============================================================
export const circleTwoClickMode = {
    onSetup() {
        const state = { center: null, previewId: null };
        this.updateUIClasses({ mouse: 'add' });
        this.setActionableState({ trash: false });
        return state;
    },

    onClick(state, e) {
        const lngLat = [e.lngLat.lng, e.lngLat.lat];

        if (!state.center) {
            // 1º clique → guarda centro e desenha um círculo de 1px como preview.
            // Cria a feature UMA vez, adiciona essa mesma e guarda o id dela
            // (antes criava duas e guardava o id da que não era adicionada → leak).
            state.center = lngLat;
            const previewFeature = this.newFeature(makeCircleFeature(state.center, 1, 'circle_preview'));
            this.addFeature(previewFeature);
            state.previewId = previewFeature.id;
            return;
        }

        // 2º clique → calcula raio e finaliza
        const radius = metersBetween(state.center, lngLat);
        const finalFeature = makeCircleFeature(state.center, radius, 'circle');
        const newFeat = this.newFeature(finalFeature);
        this.addFeature(newFeat);

        // remove preview se existir
        if (state.previewId) this.deleteFeature(state.previewId, { silent: true });

        this.map.fire('draw.create', { features: [newFeat.toGeoJSON()] });
        this.changeMode('simple_select', { featureIds: [newFeat.id] });
    },

    onMouseMove(state, e) {
        if (!state.center) return;
        // Atualiza preview do círculo seguindo o mouse
        const lngLat = [e.lngLat.lng, e.lngLat.lat];
        const radius = metersBetween(state.center, lngLat);
        if (radius < 1) return;
        const preview = makeCircleFeature(state.center, radius, 'circle_preview');
        if (state.previewId) {
            const feat = this.getFeature(state.previewId);
            if (feat) feat.setCoordinates(preview.geometry.coordinates);
        }
    },

    onStop(state) {
        // Limpa preview órfão se o usuário trocar de modo entre o 1º e o 2º clique.
        if (state?.previewId && this.getFeature(state.previewId)) {
            this.deleteFeature(state.previewId, { silent: true });
        }
    },

    toDisplayFeatures(state, geojson, display) {
        const isPreview = geojson.properties?.shapeType === 'circle_preview';
        geojson.properties.active = isPreview ? 'true' : 'false';
        display(geojson);
    },
};

// ============================================================
// Modo: marcador (clique único → círculo de raio fixo)
// ============================================================
export const markerCircleMode = {
    onSetup(opts) {
        this.updateUIClasses({ mouse: 'add' });
        this.setActionableState({ trash: false });
        return {
            radius: opts?.radius || RAIO_PADRAO_MARCADOR_M,
        };
    },

    onClick(state, e) {
        const center = [e.lngLat.lng, e.lngLat.lat];
        const feature = makeCircleFeature(center, state.radius, 'marker_circle');
        const newFeat = this.newFeature(feature);
        this.addFeature(newFeat);

        this.map.fire('draw.create', { features: [newFeat.toGeoJSON()] });
        this.changeMode('simple_select', { featureIds: [newFeat.id] });
    },

    onStop() {},

    toDisplayFeatures(state, geojson, display) {
        display(geojson);
    },
};

// ============================================================
// Custom control com botões extras (rectangle, circle, marker)
// Renderiza no mesmo grupo de controls do mapbox-gl-draw.
// ============================================================
export class DrawExtraControls {
    constructor({ draw }) {
        this.draw = draw;
    }

    onAdd(map) {
        this._map = map;
        this._container = document.createElement('div');
        this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';

        const addBtn = (title, modeName, iconText) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.title = title;
            btn.className = 'mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_' + modeName;
            btn.textContent = iconText;
            btn.style.fontSize = '14px';
            btn.style.fontWeight = 'bold';
            btn.onclick = () => this.draw.changeMode(modeName);
            this._container.appendChild(btn);
            return btn;
        };

        addBtn('Retângulo', 'draw_rectangle', '▭');
        addBtn('Círculo (2 cliques)', 'draw_circle_two_click', '◯');
        addBtn('Marcador (círculo 3km)', 'draw_marker_circle', '●');

        return this._container;
    }

    onRemove() {
        this._container?.parentNode?.removeChild(this._container);
        this._map = undefined;
    }
}
